import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid, Typography } from '@mui/material';
import { getFileSubCategoryAnalytics } from 'src/services/analytics.service';
import StackedHorizontalBarChart from 'src/sections/@dashboard/app/StackedHorizontalBarChart';


const color_map = {
    'word': '#5564aa',
    'text': '#e36d26',
    'note': '#c5a88d',
    'pdf': '#f05023',
    'excel': '#2db34b',
    'powerpoint': '#f9993a',
    'archive': '#FF4560',
    'audio': '#f9993a',
    'video': '#90278f',
    'image': '#94cd81',
    'code': '#FF4560',
    'design': '#5b5b5c',
    'cad': '#888684',
    'font': '#FF4560',
    'file': '#a59e9eff',
    'other': '#202020ff',
}

const label_map = {
    'word': 'Documents',
    'text': 'Text Files',
    'note': 'Notes',
    'pdf': 'PDFs',
    'excel': 'Spreadsheets',
    'powerpoint': 'Presentations',
    'archive': 'Archives',
    'audio': 'Audios',
    'video': 'Videos',
    'image': 'Images',
    'code': 'Codes',
    'design': 'Designs',
    'cad': 'CAD Files',
    'font': 'Fonts',
    'file': 'Other Files',
    'other': 'Others',
}

class FileSubCategoryAnalytics extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            is_percentage: this.props.is_percentage || false,
            by: this.props.by || 'count',
            by_count: [],
            by_size: [],
            by_count_percentage: [],
            by_size_percentage: []
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.by !== this.props.by || prevProps.is_percentage !== this.props.is_percentage) {
            this.setState({
                by: this.props.by,
                is_percentage: this.props.is_percentage
            });
        }
    }

    async componentDidMount() {

        let query = null;
        if (this?.props?.user_id) {
            query = {
                user_id: this.props.user_id
            };
        }
        let file_sub_category_analytics = await getFileSubCategoryAnalytics(query);
        
        if (file_sub_category_analytics.error) {
            console.error(file_sub_category_analytics.error);
            return;
        }
        file_sub_category_analytics = file_sub_category_analytics.file_sub_category_analytics;

        let by_count_array = file_sub_category_analytics.by_count || [];
        let by_size_array = file_sub_category_analytics.by_size || [];

        let by_count = [];
        let by_count_other = {
            name: label_map['other'] || 'other',
            label: label_map['other'] || 'other',
            data: [0],
            color: color_map['other'],
            stack: 'Count'
        };
        let by_count_percentage = [];
        let total_count = 0;
        
        let by_size = [];
        let by_size_other = {
            name: label_map['other'] || 'other',
            label: label_map['other'] || 'other',
            data: [0],
            color: color_map['other'],
            stack: 'Size'
        };
        let by_size_percentage = [];
        let total_size = 0;

        total_count = by_count_array.reduce((acc, item) => acc + item.count, 0);
        total_size = by_size_array.reduce((acc, item) => acc + item.size, 0);

        for(let i = 0; i < by_count_array.length; i++) {
            let item = by_count_array[i];
            if (i >= 10) {
                by_count_other.data[0] += item.count;
                continue;
            }
            by_count.push({
                name: label_map[item.file_sub_category] || item.file_sub_category,
                label: label_map[item.file_sub_category] || item.file_sub_category,
                data: [item.count],
                color: color_map[item.file_sub_category] || color_map['other'],
                stack: 'Count'
            });
            by_count_percentage.push({
                name: label_map[item.file_sub_category] || item.file_sub_category,
                label: label_map[item.file_sub_category] || item.file_sub_category,
                data: [(item.count / total_count) * 100],
                color: color_map[item.file_sub_category] || color_map['other'],
                stack: 'Count'
            });
        }
        if (by_count_other.data[0] > 0) {
            by_count.push(by_count_other);
            by_count_percentage.push({
                name: label_map['other'] || 'other',
                label: label_map['other'] || 'other',
                data: [(by_count_other.data[0] / total_count) * 100],
                color: color_map['other'],
                stack: 'Count'
            });
        }

        for(let i = 0; i < by_size_array.length; i++) {
            let item = by_size_array[i];
            if (i >= 10) {
                by_size_other.data[0] += item.size;
                continue;
            }
            by_size.push({
                name: label_map[item.file_sub_category] || item.file_sub_category,
                label: label_map[item.file_sub_category] || item.file_sub_category,
                data: [item.size],
                color: color_map[item.file_sub_category] || color_map['other'],
                stack: 'Size'
            });
            by_size_percentage.push({
                name: label_map[item.file_sub_category] || item.file_sub_category,
                label: label_map[item.file_sub_category] || item.file_sub_category,
                data: [((item.size / total_size) * 100)],
                color: color_map[item.file_sub_category] || color_map['other'],
                stack: 'Size'
            });
        }
        if (by_size_other.data[0] > 0) {
            by_size.push(by_size_other);
            by_size_percentage.push({
                name: label_map['other'] || 'other',
                label: label_map['other'] || 'other',
                data: [((by_size_other.data[0] / total_size) * 100)],
                color: color_map['other'],
                stack: 'Size'
            });
        }

        this.setState({
            by_count: by_count,
            by_size: by_size,
            by_count_percentage: by_count_percentage,
            by_size_percentage: by_size_percentage
        });

    }

    render() {

        if (this.state.by_count.length === 0 && this.state.by_size.length === 0) {
            return null;
        }

        return (
            <Grid container spacing={2}>
            
                <Grid item xs={12}>
                    <Typography variant="h5" fontWeight={400} color={'gray'}>
                        File Type Summary
                    </Typography>
                </Grid>

                {(this.state.by === 'count') ? (
                    <Grid item xs={12}>
                        <StackedHorizontalBarChart
                            series={this.state.is_percentage ? this.state.by_count_percentage : this.state.by_count}
                            labels={['Count']}
                            value_type={this.state.is_percentage ? 'percentage' : 'count'}
                        />
                    </Grid>
                ) : null}

                {(this.state.by === 'size') ? (
                    <Grid item xs={12}>
                        <StackedHorizontalBarChart
                            series={this.state.is_percentage ? this.state.by_size_percentage : this.state.by_size}
                            labels={['Size']}
                            value_type={this.state.is_percentage ? 'percentage' : 'size'}
                        />
                    </Grid>
                ) : null}                

            </Grid>
        );
    }
}

// Wrap and export
export default function (props) {
    const navigate = useNavigate();
    const params = useParams();

    return <FileSubCategoryAnalytics {...props} navigate={navigate} params={params} />;
}
